/**
 * OpenID Connect relying party for the pacmny SPA.
 *
 * Authorization Code flow with PKCE against pacmny-auth. Replaces the legacy
 * scheme entirely: the SPA used to POST a username and password to the Yii2
 * backend, hold a cookie session, and then gate two-factor *in the router* —
 * `is2faVerified` was Vuex state in sessionStorage that no backend ever saw.
 * Now the SPA never handles a password, and no token exists until the
 * authorization server has completed both factors.
 *
 * Token storage: in memory, with the refresh token in sessionStorage.
 * ---------------------------------------------------------------------------
 * Access tokens are held in a module-scoped variable so they are unreachable
 * from `localStorage` and die with the tab. The refresh token has to survive a
 * page reload for the app to be usable, so it lives in `sessionStorage` — which
 * is per-tab and cleared on close, unlike the `localStorage` the old build
 * persisted Vuex into. This is the standard trade-off for a browser client
 * without a backend-for-frontend; refresh tokens rotate on every use, so a
 * stolen one is single-shot and detectable.
 */

import {
  createCodeVerifier,
  createCodeChallenge,
  createRandomValue,
  decodeJwtPayload,
} from './pkce';

const ISSUER = (process.env.VUE_APP_OIDC_ISSUER || 'http://localhost:8100').replace(/\/$/, '');

/**
 * pacmny-auth's page for the number sign-in codes are texted to (ACC-30).
 * Only pacmny-auth moves it, after a fresh sign-in and a code sent to the new
 * number; pacmny-api refuses it on every self-service write. My Profile links
 * here rather than offering the field (main #315 let the profile edit it).
 */
export const MFA_PHONE_PAGE_URL = `${ISSUER}/security/mfa-phone`;

const CLIENT_ID = process.env.VUE_APP_OIDC_CLIENT_ID || 'pacmny-spa';
const REDIRECT_URI = process.env.VUE_APP_OIDC_REDIRECT_URI
  || `${window.location.origin}/auth/callback`;
const POST_LOGOUT_REDIRECT_URI = process.env.VUE_APP_OIDC_POST_LOGOUT_REDIRECT_URI
  || `${window.location.origin}/`;

/**
 * Everything the app can do. The AS still intersects this with what the user
 * holds, and the API re-checks the permission live on every request.
 *
 * A permission missing from this list can never reach a token, so every route
 * gated on it refuses every user — which is how a new permission silently
 * breaks a feature. The list is pinned, identically, in pacmny-auth's
 * `ScopeCatalogTest`, pacmny-api's `PermissionCatalogueTest` and
 * `src/auth/__tests__/requestedScopes.spec.js`: change all three with the
 * catalogue. Exported for that spec only.
 */
export const REQUESTED_SCOPE_LIST = Object.freeze([
  'openid', 'profile', 'email', 'pacmny_profile', 'offline_access',
  'patients:read', 'patients:write', 'patients:view_chart', 'patients:roster',
  'users:read', 'users:write', 'users:credentials', 'users:org_view',
  'users:deactivate', 'users:reactivate',
  'contacts:read', 'contacts:write',
  'medications:read', 'medications:add_unordered', 'medications:write', 'medications:import',
  'medications:administer', 'medications:prepour', 'medications:witness',
  'diagnoses:read', 'diagnoses:add', 'diagnoses:write',
  'procedures:read', 'procedures:add_unordered', 'procedures:write', 'procedures:import', 'procedures:discontinue',
  'allergies:read', 'allergies:add', 'allergies:write',
  'vitals:read', 'vitals:write',
  'immunizations:read', 'immunizations:write',
  'labs:read', 'labs:write',
  'orders:read', 'orders:write', 'orders:sign',
  'directives:read', 'directives:write', 'directives:sign', 'directives:admin',
  'directives:all_patients', 'directives:create_own',
  'documents:read', 'documents:write', 'caregiver_documents:read',
  'notes:read', 'notes:write',
  'devices:read', 'devices:add', 'devices:remove',
  'cpt_assignments:read', 'cpt_assignments:write',
  'billing:read', 'billing:write',
  'scheduling:read', 'scheduling:write', 'scheduling:admin',
  'messages:read', 'messages:write', 'patients:communicate',
  'groups:admin', 'licenses:verify', 'privileges:admin', 'privileges:report', 'audit:read', 'help:admin', 'org:admin',
  // Platform Administration (Trello nRhCnuT0). Platform-only: the API resolves
  // them for an operator of the platform tenant and for nobody else.
  'platform:admin', 'platform:tenants', 'platform:configure', 'platform:privileges', 'platform:audit',
]);

const REQUESTED_SCOPES = REQUESTED_SCOPE_LIST.join(' ');

const STORAGE = {
  verifier: 'pacmny.oidc.verifier',
  state: 'pacmny.oidc.state',
  nonce: 'pacmny.oidc.nonce',
  returnTo: 'pacmny.oidc.returnTo',
  refresh: 'pacmny.oidc.refresh',
  idToken: 'pacmny.oidc.idToken',
  // The act-as token, kept for the tab so a reload does not silently drop the
  // user back into their own identity mid-task. Its expiry rides with it
  // because the token is deliberately not refreshable.
  actAs: 'pacmny.oidc.actAs',
};

/** RFC 8693. */
const TOKEN_EXCHANGE_GRANT = 'urn:ietf:params:oauth:grant-type:token-exchange';
const ACCESS_TOKEN_TYPE = 'urn:ietf:params:oauth:token-type:access_token';

let accessToken = null;
let accessTokenExpiresAt = 0;
let accessTokenIssuedAt = 0;

/**
 * Refresh on use once the access token is this old, although it is still good.
 *
 * pacmny-auth ends a sign-in that has gone 15 minutes without a refresh
 * (oidc.session.idle_seconds; RefreshChainBounds) - main's keyon idle window.
 * The authorization server sees refreshes, not API calls, so a refresh is its
 * only sign of activity: refreshing on use once a token is a third of that
 * window old means a person active at least every ten minutes is never signed
 * out, and one silent for fifteen always is. Waiting for the token to expire
 * instead would sign out someone who had used the app a minute earlier.
 */
export const REFRESH_AFTER_MS = 5 * 60_000;

/**
 * The act-as token, when one is in play.
 *
 * Held *alongside* the user's own token rather than replacing it. Reverting is
 * then simply discarding this, with no server round trip and nothing to fail —
 * which matters, because the way out of somebody else's chart should not
 * depend on the network.
 */
let actAsToken = null;
let actAsExpiresAt = 0;
let refreshInFlight = null;
const listeners = new Set();

let discovery = null;

/** Read the provider's metadata rather than hard-coding endpoint paths. */
async function getDiscovery() {
  if (discovery) return discovery;

  const response = await fetch(`${ISSUER}/.well-known/openid-configuration`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Identity provider is unreachable (HTTP ${response.status}).`);
  }

  discovery = await response.json();
  return discovery;
}

/**
 * The sign-in redirect this page has already started, if any.
 *
 * One per page. A cold start asks for it from more than one place at once -
 * the router guard, and each API call refused for want of a token - and every
 * call used to mint its own state and send the browser to /oauth/authorize
 * again. The authorization server answered each pass by sending the person a
 * fresh second-factor code, which killed the one already in their inbox.
 * Later callers now join the first. A page restored from the back-forward
 * cache, or a redirect that failed to start, lets the next call try again.
 */
let loginInFlight = null;

if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) loginInFlight = null;
  });
}

/** Send the browser to the authorization server to sign in. */
export function beginLogin(options = {}) {
  if (!loginInFlight) {
    loginInFlight = redirectToSignIn(options).catch((e) => {
      loginInFlight = null;
      throw e;
    });
  }

  return loginInFlight;
}

async function redirectToSignIn({ returnTo, prompt } = {}) {
  const { authorization_endpoint: authorizeUrl } = await getDiscovery();

  const verifier = createCodeVerifier();
  const challenge = await createCodeChallenge(verifier);
  const state = createRandomValue();
  const nonce = createRandomValue();

  sessionStorage.setItem(STORAGE.verifier, verifier);
  sessionStorage.setItem(STORAGE.state, state);
  sessionStorage.setItem(STORAGE.nonce, nonce);
  sessionStorage.setItem(
    STORAGE.returnTo,
    returnTo || window.location.pathname + window.location.search,
  );

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: REQUESTED_SCOPES,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  if (prompt) params.set('prompt', prompt);

  window.location.assign(`${authorizeUrl}?${params.toString()}`);
}

/**
 * Handle the redirect back from the authorization server.
 * Returns the path the user was heading for before they were bounced to login.
 */
export async function completeLogin(search = window.location.search) {
  const params = new URLSearchParams(search);

  if (params.get('error')) {
    clearSession();
    throw new Error(params.get('error_description') || params.get('error'));
  }

  const code = params.get('code');
  const returnedState = params.get('state');
  const expectedState = sessionStorage.getItem(STORAGE.state);
  const verifier = sessionStorage.getItem(STORAGE.verifier);

  if (!code || !verifier) {
    throw new Error('Sign-in could not be completed. Please try again.');
  }

  // Rejecting a mismatched state is what stops an attacker from feeding the
  // browser an authorization code they obtained for their own account.
  if (!returnedState || returnedState !== expectedState) {
    clearSession();
    throw new Error('Sign-in state did not match. Please try again.');
  }

  const { token_endpoint: tokenUrl } = await getDiscovery();

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: REDIRECT_URI,
      client_id: CLIENT_ID,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    clearSession();
    throw new Error('Sign-in could not be completed. Please try again.');
  }

  const tokens = await response.json();
  const expectedNonce = sessionStorage.getItem(STORAGE.nonce);

  const claims = decodeJwtPayload(tokens.id_token);
  if (expectedNonce && claims && claims.nonce && claims.nonce !== expectedNonce) {
    clearSession();
    throw new Error('Sign-in response did not match this request.');
  }

  storeTokens(tokens);

  const returnTo = sessionStorage.getItem(STORAGE.returnTo) || '/';
  sessionStorage.removeItem(STORAGE.verifier);
  sessionStorage.removeItem(STORAGE.state);
  sessionStorage.removeItem(STORAGE.nonce);
  sessionStorage.removeItem(STORAGE.returnTo);

  return returnTo;
}

/**
 * The current access token, refreshed if it is about to expire.
 * Concurrent callers share one refresh so a burst of API calls on page load
 * does not fire a burst of refreshes (and does not race token rotation).
 */
export async function getAccessToken() {
  // Acting as someone else takes precedence, and is never refreshed.
  //
  // An act-as token is short-lived on purpose. When it lapses the right
  // outcome is to be yourself again — visibly, via `notify()`, so the UI stops
  // claiming otherwise — and NOT to quietly mint a new one: a subject
  // transition that renews itself would outlive the moment it was authorised,
  // which is the whole thing this design avoids.
  if (actAsToken) {
    if (Date.now() < actAsExpiresAt - 5_000) {
      return actAsToken;
    }

    endActAs();
    notify();
  }

  const stillGood = Boolean(accessToken) && Date.now() < accessTokenExpiresAt - 30_000;

  if (stillGood && Date.now() - accessTokenIssuedAt < REFRESH_AFTER_MS) {
    return accessToken;
  }

  if (!sessionStorage.getItem(STORAGE.refresh)) {
    return stillGood ? accessToken : null;
  }

  if (!refreshInFlight) {
    refreshInFlight = refreshTokens().finally(() => { refreshInFlight = null; });
  }

  if (!stillGood) {
    return refreshInFlight;
  }

  // An early refresh the network or a busy server (429, 5xx) could not
  // complete leaves the person on the token they already hold; a refused one
  // (400/401: the chain has ended) has already cleared the session and
  // resolved to null, and there is nothing to fall back to.
  try {
    return await refreshInFlight;
  } catch (e) {
    return accessToken;
  }
}

async function refreshTokens() {
  const refreshToken = sessionStorage.getItem(STORAGE.refresh);
  if (!refreshToken) return null;

  const { token_endpoint: tokenUrl } = await getDiscovery();

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: CLIENT_ID,
    }),
  });

  // 400 (invalid_grant: the refresh token is spent, revoked, replayed, or
  // past a sign-in bound) and 401 (invalid_client) are the authorization
  // server refusing the chain. This session is over; the caller will send
  // the user back to sign in.
  if (response.status === 400 || response.status === 401) {
    clearSession();
    notify();
    return null;
  }

  // Anything else - 429, a 5xx, a proxy's error page - is the server busy or
  // failing, not a verdict on the chain. Ending the session on it would sign
  // a person out mid-shift because of load, and most refreshes now happen
  // early, while the token in hand is still good. Throw instead: an early
  // refresh keeps that token, and an expired one surfaces the error as a
  // network failure does.
  if (!response.ok) {
    throw new Error(`Token refresh failed (HTTP ${response.status}).`);
  }

  storeTokens(await response.json());
  return accessToken;
}

function storeTokens(tokens) {
  accessToken = tokens.access_token;
  accessTokenIssuedAt = Date.now();
  accessTokenExpiresAt = accessTokenIssuedAt + ((tokens.expires_in || 900) * 1000);

  if (tokens.refresh_token) {
    sessionStorage.setItem(STORAGE.refresh, tokens.refresh_token);
  }
  if (tokens.id_token) {
    sessionStorage.setItem(STORAGE.idToken, tokens.id_token);
  }

  notify();
}

/** Identity claims for rendering. Never for authorization decisions. */
export function getIdentity() {
  const idToken = sessionStorage.getItem(STORAGE.idToken);
  return idToken ? decodeJwtPayload(idToken) : null;
}

export function isAuthenticated() {
  return Boolean(accessToken || sessionStorage.getItem(STORAGE.refresh));
}

/** End the session at the provider as well as locally, revoking tokens. */
export async function logout() {
  const idToken = sessionStorage.getItem(STORAGE.idToken);
  let endSessionUrl = `${ISSUER}/oauth/logout`;

  try {
    const meta = await getDiscovery();
    endSessionUrl = meta.end_session_endpoint || endSessionUrl;
  } catch (e) {
    /* provider unreachable; still clear locally below */
  }

  clearSession();

  const params = new URLSearchParams({
    post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,
  });
  if (idToken) params.set('id_token_hint', idToken);

  window.location.assign(`${endSessionUrl}?${params.toString()}`);
}

/**
 * Begin acting as another user — RFC 8693 token exchange.
 *
 * The caller's own access token is presented as `subject_token` and the target
 * is named directly. What comes back is a separate short-lived token; the
 * caller's own is untouched, so this is additive rather than a change of
 * identity that would have to be undone.
 *
 * The provider decides whether it is allowed — an active care relationship with
 * that chart, same organization — and records the attempt either way. A refusal
 * arrives as a 4xx and is deliberately uninformative: whether a user id exists
 * is not something a caller who may not act as them gets to learn.
 *
 * Nothing in the SPA calls this. Entering a patient's chart is viewing it, not
 * becoming the patient (decision D4, main's PR #249): the bridge's
 * `enterPatientChart` keeps the clinician's own token. pacmny-auth only offers
 * the grant when `OIDC_TOKEN_EXCHANGE_ENABLED` is set, which by default it is
 * not, so a call here is refused like any unknown grant.
 *
 * @param {number|string} targetUserId
 * @returns {Promise<boolean>} whether the transition happened
 */
export async function actAs(targetUserId) {
  const own = await getAccessToken();

  if (!own) return false;

  if (actAsToken) {
    // Already acting. Chaining is refused at the provider too; failing here
    // saves a round trip and keeps `act` meaning "the human at the keyboard".
    return false;
  }

  const { token_endpoint: tokenUrl } = await getDiscovery();

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: TOKEN_EXCHANGE_GRANT,
      client_id: CLIENT_ID,
      subject_token: own,
      subject_token_type: ACCESS_TOKEN_TYPE,
      requested_token_type: ACCESS_TOKEN_TYPE,
      requested_subject: String(targetUserId),
    }),
  });

  if (!response.ok) return false;

  const tokens = await response.json();

  if (!tokens.access_token) return false;

  actAsToken = tokens.access_token;
  actAsExpiresAt = Date.now() + ((tokens.expires_in || 600) * 1000);

  try {
    sessionStorage.setItem(STORAGE.actAs, JSON.stringify({
      token: actAsToken,
      expiresAt: actAsExpiresAt,
    }));
  } catch (e) { /* private mode; it just will not survive a reload */ }

  notify();

  return true;
}

/**
 * Stop acting as someone.
 *
 * Local and immediate: the act-as token is simply discarded and the user's own
 * is already in hand. Nothing is asked of the server, so this cannot fail —
 * which is the point. Leaving somebody else's chart must not depend on a
 * request succeeding.
 */
export function revertToSelf() {
  if (!actAsToken) return false;

  endActAs();
  notify();

  return true;
}

/** Whether this session is currently acting as another user. */
export function isActingAs() {
  return Boolean(actAsToken) && Date.now() < actAsExpiresAt;
}

/** Drop the act-as token. Deliberately silent — callers decide about notify(). */
function endActAs() {
  actAsToken = null;
  actAsExpiresAt = 0;

  try {
    sessionStorage.removeItem(STORAGE.actAs);
  } catch (e) { /* nothing to clean up */ }
}

/** Restore an act-as session across a page reload. */
function restoreActAs() {
  try {
    const raw = sessionStorage.getItem(STORAGE.actAs);

    if (!raw) return;

    const saved = JSON.parse(raw);

    if (saved && saved.token && Date.now() < (saved.expiresAt || 0) - 5_000) {
      actAsToken = saved.token;
      actAsExpiresAt = saved.expiresAt;

      return;
    }

    endActAs();
  } catch (e) {
    endActAs();
  }
}

restoreActAs();

/**
 * Browser storage that holds patient data outside the session, purged by clearSession.
 *
 * `mediaProto:` and the `mediaProto` database are the media viewer prototype's (main #343): D6 default
 * 2026-10-21, pending product confirmation. Main keeps them across sign-out; the port is stricter on PHI at
 * rest, as it is for `ppProto:` (D2), at the cost that a reviewer's local prototype data is gone at each
 * sign-out. These two constants are the one place to switch it: drop the entries to keep them.
 */
const BROWSER_PHI_PREFIXES = ['ppProto:', 'mediaProto:'];
const BROWSER_PHI_DATABASES = ['mediaProto'];

export function clearSession() {
  accessToken = null;
  accessTokenExpiresAt = 0;
  accessTokenIssuedAt = 0;
  endActAs();
  Object.values(STORAGE).forEach((key) => sessionStorage.removeItem(key));

  // The open chart goes with the session. It is only an id and the API
  // re-authorizes it on every request, so what is left behind is not a
  // disclosure — but the next person to sign in on this tab must not land in
  // the previous user's chart, and if they happen to share the caseload they
  // otherwise would.
  sessionStorage.removeItem('pacmny.chart.patientId');

  // The Vuex application model goes too.
  //
  // `vuex-persistedstate` mirrors `login.phrData.user` / `.manager` /
  // `.patient` into this key, and it is rehydrated at store construction —
  // before any router guard runs. The guard only refetches the model when
  // `phrData.user` is absent, so a surviving copy is never refreshed: the next
  // person to sign in on this tab gets their own permissions and chart but the
  // *previous* user's name, date of birth and organization on the profile
  // screen and in the navbar. Two halves of the screen describing different
  // people is worse than either being blank.
  sessionStorage.removeItem('vuex');

  // Patient data the browser kept outside the session (9Uks65wT, MED-12, D2):
  // MedAdmin's old `medications` cache, and every `ppProto:` key the pre-pour
  // page writes — pours, pre-poured administrations, discontinues, statuses,
  // the change log and the draft, keyed per account and patient, plus
  // `ppProto:autofill`. They held medication names, doses, signers and
  // reasons, survived sign-out, and the page's own Reset clears neither the
  // status key nor other patients' keys. Every key with the prefix goes, in
  // both stores. The media viewer prototype (main #343) keeps the chart
  // patient's name, date of birth and gender under `mediaProto:` and any
  // picture taken or imported in its IndexedDB database, so those go the same
  // way: they must not outlive the session on a shared device (D6 default,
  // see BROWSER_PHI_PREFIXES). Storage access can throw (a private window, blocked site
  // data); a sign-out must still complete.
  try {
    localStorage.removeItem('medications');
    sessionStorage.removeItem('medications');

    const isBrowserPhi = (k) => k && (BROWSER_PHI_PREFIXES.some((prefix) => k.startsWith(prefix)) || k === 'medications');

    const purgeKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (isBrowserPhi(k)) {
        purgeKeys.push(k);
      }
    }
    purgeKeys.forEach((k) => localStorage.removeItem(k));

    const sessionPurgeKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (isBrowserPhi(k)) {
        sessionPurgeKeys.push(k);
      }
    }
    sessionPurgeKeys.forEach((k) => sessionStorage.removeItem(k));
  } catch (e) {
    // Storage access might be restricted in some environments
  }

  try {
    BROWSER_PHI_DATABASES.forEach((name) => {
      if (typeof indexedDB !== 'undefined') indexedDB.deleteDatabase(name);
    });
  } catch (e) {
    // IndexedDB can be unavailable too (a private window); the keys above are already gone.
  }
}

/** Subscribe to token changes (used by stores to react to sign-out). */
export function onSessionChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((listener) => {
    try { listener(isAuthenticated()); } catch (e) { /* listener errors are not ours */ }
  });
}

export default {
  beginLogin,
  completeLogin,
  getAccessToken,
  getIdentity,
  isAuthenticated,
  actAs,
  revertToSelf,
  isActingAs,
  logout,
  clearSession,
  onSessionChange,
};
